import React from 'react';
import { Link } from 'react-router-dom';
import {
  SparklesIcon,
  UserGroupIcon,
  LightBulbIcon,
  HeartIcon,
  TrophyIcon,
  GlobeAltIcon,
  CheckCircleIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import Navigation from './Navigation';
import Footer from './Footer';

const AboutUs = () => {
  const values = [
    {
      icon: <LightBulbIcon className="w-8 h-8" />,
      title: "Sáng tạo",
      description: "Chúng tôi luôn tìm kiếm những giải pháp sáng tạo và đột phá để mang lại giá trị tốt nhất cho khách hàng."
    },
    {
      icon: <HeartIcon className="w-8 h-8" />,
      title: "Tận tâm",
      description: "Sự thành công của khách hàng chính là động lực và mục tiêu hàng đầu của chúng tôi."
    },
    {
      icon: <TrophyIcon className="w-8 h-8" />,
      title: "Chất lượng",
      description: "Chúng tôi cam kết giao hàng đúng thời gian với chất lượng cao nhất và hỗ trợ tận tình."
    },
    {
      icon: <GlobeAltIcon className="w-8 h-8" />,
      title: "Toàn cầu",
      description: "Với tầm nhìn toàn cầu, chúng tôi mang đến những công nghệ tiên tiến nhất cho thị trường Việt Nam."
    }
  ];

  const teamMembers = [
    {
      name: "Võ Duy Thiện",
      position: "CEO & Founder",
      bio: "Với hơn 8 năm kinh nghiệm trong lĩnh vực công nghệ thông tin, anh Thiện đã dẫn dắt Smartera phát triển từ năm 2017 đến nay.",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=300&fit=crop&crop=face",
      skills: ["Leadership", "Strategy", "Business Development"],
      portfolio: "https://voduythien.myresumes.work/"
    },
    {
      name: "Nguyễn Quốc Huy",
      position: "Senior Software Engineer",
      bio: "Chuyên gia phát triển phần mềm với kinh nghiệm sâu rộng trong các công nghệ hiện đại, đảm bảo chất lượng và hiệu suất của mọi dự án.",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&h=300&fit=crop&crop=face",
      skills: ["Full-stack Development", "System Architecture", "DevOps"],
      portfolio: "https://nguyenquochuy.myresumes.work/"
    },
    {
      name: "Vũ Ngọc Thảo",
      position: "Project Manager",
      bio: "Chuyên gia quản lý dự án với kỹ năng tổ chức và điều phối xuất sắc, đảm bảo mọi dự án được hoàn thành đúng tiến độ và chất lượng.",
      image: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=300&h=300&fit=crop&crop=face",
      skills: ["Project Management", "Team Coordination", "Quality Assurance"],
      portfolio: "https://thaovu.myresumes.work/"
    }
  ];

  const milestones = [
    {
      year: "2017",
      title: "Thành lập Smartera",
      description: "Khởi đầu với đội ngũ 3 người và tầm nhìn đưa công nghệ Việt ra thế giới."
    },
    {
      year: "2019",
      title: "Mở rộng đội ngũ",
      description: "Phát triển đội ngũ và hoàn thành 50+ dự án website và ứng dụng mobile cho các doanh nghiệp."
    },
    {
      year: "2021",
      title: "Mở rộng quốc tế",
      description: "Thiết lập mặt bằng quốc tế với website smartera.uk và phục vụ khách hàng toàn cầu."
    },
    {
      year: "2023",
      title: "Đa dạng hóa dịch vụ",
      description: "Mở rộng sang digital marketing, hosting và các giải pháp chuyển đổi số toàn diện."
    },
    {
      year: "2025",
      title: "Hiện tại",
      description: "150+ dự án hoàn thành, 80+ khách hàng tin tưởng với văn phòng tại TP.HCM và Đà Nẵng."
    }
  ];

  return (
    <div className="min-h-screen bg-dark-900">
      {/* Navigation */}
      <Navigation />

      {/* Hero Section */}
      <section className="pt-16 bg-gradient-to-br from-dark-900 via-dark-800 to-ocean-900 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Về <span className="text-white">Smart</span><span className="text-cyan-400">era</span>
            </h1>
            <p className="text-xl text-ocean-100/70 max-w-3xl mx-auto">
              Chúng tôi là một đội ngũ đầy nhiệt huyết, cam kết mang đến những giải pháp công nghệ
              tốt nhất giúp doanh nghiệp phát triển bền vững trong kỷ nguyên số.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-dark-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-white mb-6">Sứ mệnh của chúng tôi</h2>
              <p className="text-lg text-ocean-100/70 mb-6">
                Smartera được thành lập với sứ mệnh đưa công nghệ tiên tiến đến gần hơn với
                các doanh nghiệp Việt Nam, giúp họ cạnh tranh hiệu quả trên thị trường toàn cầu.
              </p>
              <p className="text-lg text-ocean-100/70 mb-8">
                Chúng tôi tin rằng công nghệ không chỉ là công cụ, mà còn là cầu nối giúp
                các doanh nghiệp kết nối với khách hàng, tối ưu hóa quy trình và tăng trưởng bền vững.
              </p>
              <div className="space-y-4">
                <div className="flex items-center">
                  <CheckCircleIcon className="w-6 h-6 text-cyan-400 mr-3" />
                  <span className="text-ocean-100/80">Giải pháp công nghệ phù hợp với từng doanh nghiệp</span>
                </div>
                <div className="flex items-center">
                  <CheckCircleIcon className="w-6 h-6 text-cyan-400 mr-3" />
                  <span className="text-ocean-100/80">Hỗ trợ tận tình trong suốt quá trình phát triển</span>
                </div>
                <div className="flex items-center">
                  <CheckCircleIcon className="w-6 h-6 text-cyan-400 mr-3" />
                  <span className="text-ocean-100/80">Đảm bảo chất lượng và thời gian giao hàng</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&h=400&fit=crop"
                alt="Team collaboration"
                className="rounded-2xl shadow-xl border border-cyan-500/20"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-dark-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Giá trị cốt lõi
            </h2>
            <p className="text-xl text-ocean-100/60">
              Những giá trị định hướng mọi hoạt động của chúng tôi
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-dark-800/50 backdrop-blur-xl p-8 rounded-xl border border-cyan-500/20 text-center hover:border-cyan-500/40 transition-all">
                <div className="text-cyan-400 mb-4 flex justify-center">
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{value.title}</h3>
                <p className="text-ocean-100/60">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-dark-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Hành trình phát triển
            </h2>
            <p className="text-xl text-ocean-100/60">
              Từ những ngày đầu tiên đến hiện tại
            </p>
          </div>

          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-cyan-500/20"></div>
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <div key={index} className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                  <div className={`w-1/2 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8 text-left'}`}>
                    <div className="bg-dark-800/50 backdrop-blur-xl p-6 rounded-xl border border-cyan-500/20">
                      <div className="text-2xl font-bold text-cyan-400 mb-2">{milestone.year}</div>
                      <h3 className="text-xl font-bold text-white mb-2">{milestone.title}</h3>
                      <p className="text-ocean-100/60">{milestone.description}</p>
                    </div>
                  </div>
                  <div className="relative flex items-center justify-center w-8 h-8">
                    <div className="w-4 h-4 bg-cyan-400 rounded-full border-4 border-dark-900 shadow-glow-cyan"></div>
                  </div>
                  <div className="w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 bg-dark-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Đội ngũ của chúng tôi
            </h2>
            <p className="text-xl text-ocean-100/60">
              Những con người tài năng đứng sau thành công của Smartera
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {teamMembers.map((member, index) => (
              <div key={index} className="bg-dark-800/50 backdrop-blur-xl rounded-xl border border-cyan-500/20 overflow-hidden hover:border-cyan-500/40 transition-all">
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-64 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-1">{member.name}</h3>
                  <p className="text-cyan-400 font-medium mb-3">{member.position}</p>
                  <p className="text-ocean-100/60 text-sm mb-4">{member.bio}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {member.skills.map((skill, idx) => (
                      <span key={idx} className="px-2 py-1 bg-cyan-500/10 text-cyan-400 text-xs rounded-full border border-cyan-500/20">
                        {skill}
                      </span>
                    ))}
                  </div>
                  {member.portfolio && (
                    <a
                      href={member.portfolio}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-cyan-400 hover:text-cyan-300 text-sm font-medium inline-flex items-center"
                    >
                      Xem hồ sơ <ArrowRightIcon className="w-4 h-4 ml-1" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-cyan-600 to-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Sẵn sàng hợp tác cùng chúng tôi?
          </h2>
          <p className="text-xl text-cyan-100 mb-8 max-w-2xl mx-auto">
            Hãy để Smartera đồng hành cùng bạn trong hành trình chuyển đổi số và phát triển doanh nghiệp.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-white text-cyan-600 px-8 py-3 rounded-xl hover:bg-gray-100 transition-colors inline-flex items-center justify-center font-semibold"
            >
              Liên hệ với chúng tôi <ArrowRightIcon className="w-5 h-5 ml-2" />
            </Link>
            <Link
              to="/"
              className="border border-white text-white px-8 py-3 rounded-xl hover:bg-white/10 transition-colors inline-flex items-center justify-center font-semibold"
            >
              Xem dịch vụ
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default AboutUs;
